import React, { useState } from 'react';
import StandingsPanel from './StandingsPanel.jsx';
import HubLinkBadges from './HubLinkBadges.jsx';
import HubRemindButton from './HubRemindButton.jsx';
import { hasNextEvent, hasStandings, hasLinks } from '../../utils/entityHubRenderable';
import { resolveNextEventChannel, resolveNextEventRemindEpg } from '../../utils/entityHubChannel';
import { formatRelativeDayLabel } from '../../utils/relativeDay';
import { cssUrlValue } from '../../utils/mediaUrl';

function formatKickoff(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const day = formatRelativeDayLabel(d, { weekday: 'short' });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${day} · ${time}`;
  } catch {
    return null;
  }
}

function Crest({ url, name }) {
  if (!url) return null;
  return (
    <span className="entity-hub-crest-wrap">
      <img className="entity-hub-crest" src={url} alt={name || ''} />
    </span>
  );
}

function OffAirBadge() {
  return (
    <span className="entity-hub-off-air" aria-label="Off air">
      <svg
        className="entity-hub-off-air-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3.5" y="7" width="17" height="11" rx="2" />
        <path d="M8 7l2.5-2.5M16 7l-2.5-2.5" />
        <path d="M4.5 19.5l15-15" />
      </svg>
      <span className="entity-hub-off-air-label">Off air</span>
    </span>
  );
}

function KickoffBlock({ kickoff, channel }) {
  if (!kickoff && !channel) return null;
  return (
    <div className="entity-hub-kickoff">
      {kickoff ? (
        <span className="entity-hub-vs-time">{kickoff}</span>
      ) : null}
      {channel ? (
        <div className="entity-hub-broadcast">
          {channel.logo ? (
            <img
              className="entity-hub-broadcast-logo"
              src={channel.logo}
              alt={channel.name || 'Channel'}
            />
          ) : null}
          {channel.name ? (
            <span className="entity-hub-broadcast-name">{channel.name}</span>
          ) : null}
        </div>
      ) : kickoff ? (
        <OffAirBadge />
      ) : null}
    </div>
  );
}

function SportsHero({ hub, results }) {
  const [remindOpen, setRemindOpen] = useState(false);
  const event = hub.nextEvent;
  const kickoff = formatKickoff(event?.startsAt);
  const channel = resolveNextEventChannel(event);
  const remindEpg = resolveNextEventRemindEpg(event, results);
  const venueCss = cssUrlValue(event?.venueImageUrl);
  const venueStyle = venueCss
    ? { '--hub-venue-image': venueCss }
    : undefined;

  const hasSides = Boolean(event?.home?.name || event?.away?.name);
  const isTitleLayout =
    event?.kind === 'race' ||
    event?.kind === 'concert' ||
    (!hasSides && Boolean(event?.title));

  const competitionName = event?.competition?.name;
  const heroMeta =
    [event?.venueName, competitionName].filter(Boolean).join(' · ')
    || event?.title
    || 'Next event';
  // Avoid repeating competition/entity name already shown in the top meta.
  const showFooterCompetitionName =
    Boolean(competitionName) &&
    !heroMeta.toLowerCase().includes(String(competitionName).toLowerCase());
  const showFooterCompetition =
    Boolean(event?.competition?.badgeUrl) || showFooterCompetitionName;

  return (
    <div
      className={`entity-hub-panel entity-hub-hero${remindOpen ? ' entity-hub-hero--remind-open' : ''}`}
      style={venueStyle}
    >
      <HubRemindButton
        epgEvent={remindEpg}
        onOpenChange={setRemindOpen}
      />

      <div className="entity-hub-hero-meta">{heroMeta}</div>

      {isTitleLayout ? (
        <div className="entity-hub-matchup">
          <div className="entity-hub-vs">
            <span className="entity-hub-vs-label">Next</span>
            <span className="entity-hub-vs-text">{event.title || hub.entity?.name}</span>
            <KickoffBlock kickoff={kickoff} channel={channel} />
          </div>
        </div>
      ) : (
        <div className="entity-hub-matchup">
          <div className="entity-hub-side">
            <Crest url={event?.home?.crestUrl} name={event?.home?.name} />
            <span className="entity-hub-side-name">{event?.home?.name || 'Home'}</span>
          </div>
          <div className="entity-hub-vs">
            <span className="entity-hub-vs-label">Next</span>
            <span className="entity-hub-vs-text">vs</span>
            <KickoffBlock kickoff={kickoff} channel={channel} />
          </div>
          <div className="entity-hub-side">
            <Crest url={event?.away?.crestUrl} name={event?.away?.name} />
            <span className="entity-hub-side-name">{event?.away?.name || 'Away'}</span>
          </div>
        </div>
      )}

      <div className="entity-hub-hero-footer">
        {showFooterCompetition ? (
          <div className="entity-hub-badge-row">
            {event?.competition?.badgeUrl ? (
              <img
                className="entity-hub-comp-badge"
                src={event.competition.badgeUrl}
                alt=""
              />
            ) : null}
            {showFooterCompetitionName ? <span>{competitionName}</span> : null}
          </div>
        ) : (
          <span />
        )}
        {hasLinks(hub) ? <HubLinkBadges links={hub.links} /> : null}
      </div>
    </div>
  );
}

function SportsHub({ hub, entityId, results }) {
  const showHero = hasNextEvent(hub);
  const showTable = hasStandings(hub);
  const single = (showHero && !showTable) || (!showHero && showTable);

  return (
    <div
      className={`entity-hub-grid${single ? ' entity-hub-grid--single' : ''}`}
    >
      {showHero ? <SportsHero hub={hub} results={results} /> : null}
      {showTable ? <StandingsPanel hub={hub} entityId={entityId} /> : null}
    </div>
  );
}

export default SportsHub;
